import { Dictionary } from 'lodash';
import { TimesheetEntryType } from 'api/timesheets';
import { Category, CategoryId } from 'api/timesheets/categories';
import {
  TimesheetCategoryReport,
  TimesheetPhaseReport,
  TimesheetProjectReport,
  TimesheetReport,
  TimesheetTaskReport,
  TimesheetUserReport,
} from 'api/timesheets/reports/types';
import {
  calculatePercentage,
  calculateUtilization,
  getTotalMinutes,
  getTotalMinutesForCategory,
} from 'components/organisms/timesheets/common/utils';
import { fullName } from 'utils/full-name';
import {
  TimesheetAdhocTask,
  TimesheetCalendarTask,
  TimesheetTask,
} from '../../types';

const getTaskName = (
  task: TimesheetTask | TimesheetAdhocTask | TimesheetCalendarTask,
) =>
  task
    ? task.entryType === TimesheetEntryType.TaskEntry
      ? task.name
      : task.activityName
    : '';

export const projectNameSorter = (
  { project: projectA }: TimesheetProjectReport,
  { project: projectB }: TimesheetProjectReport,
): number => {
  if (!projectA) {
    return 1;
  }
  if (!projectB) {
    return -1;
  }

  return projectA.projectName.localeCompare(projectB.projectName);
};

export const phaseNameSorter = (
  { projectPhase: projectPhaseA }: TimesheetPhaseReport,
  { projectPhase: projectPhaseB }: TimesheetPhaseReport,
): number => {
  if (!projectPhaseA) {
    return 1;
  }
  if (!projectPhaseB) {
    return -1;
  }

  return projectPhaseA.projectPhaseName?.localeCompare(
    projectPhaseB.projectPhaseName,
  );
};

export const userNameSorter = (
  {
    user: userA,
    billableMinutes: billableMinutesA,
    nonBillableMinutes: nonBillableMinutesA,
  }: TimesheetUserReport,
  {
    user: userB,
    billableMinutes: billableMinutesB,
    nonBillableMinutes: nonBillableMinutesB,
  }: TimesheetUserReport,
): number => {
  const totalMinutesA = billableMinutesA + nonBillableMinutesA;
  const totalMinutesB = billableMinutesB + nonBillableMinutesB;

  if (
    (totalMinutesA === 0 && totalMinutesB === 0) ||
    (totalMinutesA !== 0 && totalMinutesB !== 0)
  ) {
    return userA && userB && fullName(userA).localeCompare(fullName(userB));
  }
  if (totalMinutesA === 0) {
    return 1;
  }
  if (totalMinutesB === 0) {
    return -1;
  }

  return 0;
};

export const taskNameSorter = (
  { task: taskA }: TimesheetTaskReport,
  { task: taskB }: TimesheetTaskReport,
): number => getTaskName(taskA)?.localeCompare(getTaskName(taskB));

export const categoryNameSorter =
  (categoriesLookup: Dictionary<Category>) =>
  (
    {
      category: categoryA,
      billableMinutes: billableMinutesA,
      nonBillableMinutes: nonBillableMinutesA,
    }: TimesheetCategoryReport,
    {
      category: categoryB,
      billableMinutes: billableMinutesB,
      nonBillableMinutes: nonBillableMinutesB,
    }: TimesheetCategoryReport,
  ): number => {
    const totalMinutesA = billableMinutesA + nonBillableMinutesA;
    const totalMinutesB = billableMinutesB + nonBillableMinutesB;

    if (
      (totalMinutesA === 0 && totalMinutesB === 0) ||
      (totalMinutesA !== 0 && totalMinutesB !== 0)
    ) {
      if (!categoryA) {
        return 1;
      }
      if (!categoryB) {
        return -1;
      }
      const categoryADeleted = categoriesLookup[categoryA.categoryId].deleted;
      const categoryBDeleted = categoriesLookup[categoryB.categoryId].deleted;

      if (categoryADeleted && !categoryBDeleted) {
        return 1;
      }
      if (!categoryADeleted && categoryBDeleted) {
        return -1;
      }

      return categoryA.categoryName.localeCompare(categoryB.categoryName);
    }
    if (totalMinutesA === 0) {
      return 1;
    }
    if (totalMinutesB === 0) {
      return -1;
    }

    return 0;
  };

export const totalMinutesSorter = (
  {
    billableMinutes: billableMinutesA,
    nonBillableMinutes: nonBillableMinutesA,
  }: TimesheetReport,
  {
    billableMinutes: billableMinutesB,
    nonBillableMinutes: nonBillableMinutesB,
  }: TimesheetReport,
): number =>
  getTotalMinutes(billableMinutesA, nonBillableMinutesA) -
  getTotalMinutes(billableMinutesB, nonBillableMinutesB);
export const billableMinutesSorter = (
  {
    billableMinutes: billableMinutesA,
    nonBillableMinutes: nonBillableMinutesA,
  }: TimesheetReport,
  {
    billableMinutes: billableMinutesB,
    nonBillableMinutes: nonBillableMinutesB,
  }: TimesheetReport,
): number =>
  calculatePercentage(billableMinutesA, billableMinutesA, nonBillableMinutesA) -
  calculatePercentage(billableMinutesB, billableMinutesB, nonBillableMinutesB);

export const nonBillableMinutesSorter = (
  {
    billableMinutes: billableMinutesA,
    nonBillableMinutes: nonBillableMinutesA,
  }: TimesheetReport,
  {
    billableMinutes: billableMinutesB,
    nonBillableMinutes: nonBillableMinutesB,
  }: TimesheetReport,
): number =>
  calculatePercentage(
    nonBillableMinutesA,
    billableMinutesA,
    nonBillableMinutesA,
  ) -
  calculatePercentage(
    nonBillableMinutesB,
    billableMinutesB,
    nonBillableMinutesB,
  );

export const categoryMinutesSorter = (
  {
    billableMinutes: billableMinutesA,
    nonBillableMinutes: nonBillableMinutesA,
    categories: categoriesA,
  }: TimesheetProjectReport | TimesheetTaskReport | TimesheetUserReport,
  {
    billableMinutes: billableMinutesB,
    nonBillableMinutes: nonBillableMinutesB,
    categories: categoriesB,
  }: TimesheetProjectReport | TimesheetTaskReport | TimesheetUserReport,
  categoryId: CategoryId,
): number =>
  calculatePercentage(
    getTotalMinutesForCategory(categoriesA ?? [], categoryId),
    billableMinutesA,
    nonBillableMinutesA,
  ) -
  calculatePercentage(
    getTotalMinutesForCategory(categoriesB ?? [], categoryId),
    billableMinutesB,
    nonBillableMinutesB,
  );

export const nonCategoryMinutesSorter = (
  {
    billableMinutes: billableMinutesA,
    nonBillableMinutes: nonBillableMinutesA,
    categories: categoriesA,
  }: TimesheetProjectReport | TimesheetTaskReport | TimesheetUserReport,
  {
    billableMinutes: billableMinutesB,
    nonBillableMinutes: nonBillableMinutesB,
    categories: categoriesB,
  }: TimesheetProjectReport | TimesheetTaskReport | TimesheetUserReport,
): number =>
  calculatePercentage(
    getTotalMinutesForCategory(categoriesA ?? []),
    billableMinutesA,
    nonBillableMinutesA,
  ) -
  calculatePercentage(
    getTotalMinutesForCategory(categoriesB ?? []),
    billableMinutesB,
    nonBillableMinutesB,
  );

export const utilizationSorter = (
  { billableMinutes: billableMinutesA }: TimesheetReport,
  { billableMinutes: billableMinutesB }: TimesheetReport,
  capacityInMinutes: number,
): number =>
  calculateUtilization(billableMinutesA, capacityInMinutes) -
  calculateUtilization(billableMinutesB, capacityInMinutes);
